// pdf-lib's standard fonts encode text as WinAnsi (CP1252). Any character
// outside that set throws "WinAnsi cannot encode ..." and aborts the entire
// PDF generation. Teachers routinely paste text from Word / Google Docs / phone
// keyboards (and AI-generated comments contain typographic punctuation), so
// curly quotes, dashes, ellipses, etc. reach the form fields constantly.
//
// This sanitizer runs on every value just before setText():
//   1. Transliterate common typographic characters to WinAnsi-safe equivalents
//      (curly quote -> straight quote, em-dash -> "--", etc.) so the output is
//      visually faithful and lossless for the cases that actually occur.
//   2. As a final safety net, drop any remaining code point WinAnsi can't
//      encode so generation can never crash on an unforeseen character.

// Explicit transliterations for the punctuation that shows up in practice.
const REPLACEMENTS = {
  // Single quotes / apostrophes
  '‘': "'", // ' left single quotation
  '’': "'", // ' right single quotation (smart apostrophe)
  '‚': "'", // ‚ single low-9 quotation
  '‛': "'", // ‛ single high-reversed-9
  '′': "'", // ′ prime
  'ʹ': "'", // ʹ modifier letter prime
  'ʻ': "'", // ʻ modifier letter turned comma
  'ʼ': "'", // ʼ modifier letter apostrophe
  'ʾ': "'", // ʾ modifier letter right half ring  <-- the reported crash
  'ʿ': "'", // ʿ modifier letter left half ring
  '´': "'", // ´ acute accent used as apostrophe
  '`': "'", // ` grave accent used as apostrophe
  // Double quotes
  '“': '"', // " left double quotation
  '”': '"', // " right double quotation
  '„': '"', // „ double low-9 quotation
  '‟': '"', // ‟ double high-reversed-9
  '″': '"', // ″ double prime
  '«': '"', // « left guillemet
  '»': '"', // » right guillemet
  // Dashes / hyphens
  '‐': '-', // ‐ hyphen
  '‑': '-', // ‑ non-breaking hyphen
  '‒': '-', // ‒ figure dash
  '–': '-', // – en dash
  '—': '--', // — em dash
  '―': '--', // ― horizontal bar
  '−': '-', // − minus sign
  // Spaces
  ' ': ' ', // non-breaking space
  ' ': ' ', // figure space
  ' ': ' ', // thin space
  ' ': ' ', // hair space
  ' ': ' ', // narrow no-break space
  '​': '', //  zero-width space (drop)
  '﻿': '', // zero-width no-break space / BOM (drop)
  // Other punctuation
  '…': '...', // … horizontal ellipsis
  '•': '-', // • bullet
  '·': '-', // · middle dot
  '‧': '-', // ‣ hyphenation point
  '⁄': '/', // ⁄ fraction slash
};

const REPLACE_RE = new RegExp(`[${Object.keys(REPLACEMENTS).join('')}]`, 'g');

// WinAnsi (CP1252) covers Latin-1 (<= U+00FF) plus a handful of code points in
// the U+0152..U+2122 range mapped into the 0x80-0x9F byte slots. Code points in
// this set are safe; everything else outside Latin-1 must be dropped.
const WINANSI_EXTRAS = new Set([
  0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030,
  0x0160, 0x2039, 0x0152, 0x017d, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022,
  0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0x017e, 0x0178,
]);

function isWinAnsiEncodable(codePoint) {
  // Control chars 0x00-0x1F (except tab/newline) aren't drawable but don't throw;
  // leave them to pdf-lib. The encoder only throws on code points it has no glyph
  // for: anything > 0xFF that isn't one of the CP1252 extras.
  if (codePoint <= 0x00ff) return true;
  return WINANSI_EXTRAS.has(codePoint);
}

// Hex code point, zero-padded to at least 4 digits: 0x0101 -> "0101".
function cpHex(codePoint) {
  return codePoint.toString(16).toUpperCase().padStart(4, '0');
}

/**
 * Make a string safe to pass to pdf-lib's setText() with a standard font.
 * Transliterates common typographic characters, then replaces anything WinAnsi
 * still cannot encode with a visible `[?]` placeholder — so the character is
 * flagged in the document itself rather than silently vanishing (the editor's
 * toast names the exact code point). Always returns a string (null -> "").
 */
export const UNSUPPORTED_PLACEHOLDER = '[?]';

export function sanitizeForPdf(value) {
  if (value == null) return '';
  const str = String(value).replace(REPLACE_RE, (ch) => REPLACEMENTS[ch]);

  let out = '';
  for (const ch of str) {
    if (isWinAnsiEncodable(ch.codePointAt(0))) out += ch;
    else out += UNSUPPORTED_PLACEHOLDER; // unencodable: visible flag, never crashes
  }
  return out;
}

/**
 * Return the unique characters in `value` that sanitizeForPdf() will silently
 * DROP from the PDF — i.e. not WinAnsi-encodable and not transliterated to an
 * ASCII equivalent. Used to warn the user about characters that won't appear.
 * Cleanly-substituted punctuation (curly quotes, dashes, ellipsis) and
 * intentionally-removed zero-width characters are NOT reported.
 */
export function findUnsupportedChars(value) {
  if (value == null) return [];
  const found = new Set();
  for (const ch of String(value)) {
    if (Object.prototype.hasOwnProperty.call(REPLACEMENTS, ch)) continue; // transliterated/handled
    if (isWinAnsiEncodable(ch.codePointAt(0))) continue; // renders fine
    found.add(ch);
  }
  return [...found];
}

/**
 * Human-readable label for a character in a notification, e.g. `ā (U+0101)`.
 */
export function describeChar(ch) {
  return `${ch} (U+${cpHex(ch.codePointAt(0))})`;
}

export default sanitizeForPdf;
